//GS1-128 application identifier parsing, used by the 3PL tally screen to read meat barcodes.
//extractAis does the raw slicing; the named fields on Gs1Barcode are the standard mapping,
//which PDATallyGS1BarcodeRules can override per customer (see Gs1Rules.ts).

export interface Gs1Barcode {
  gtin?: string;
  serialNo?: string;
  batch?: string;
  netWeight?: number; //kg, decimal place count comes from the AI itself (310n)
  useByDate?: string; //yyyy-mm-dd, AI (17)
  mfgDate?: string; //yyyy-mm-dd, AI (11)
  packDate?: string; //yyyy-mm-dd, AI (13) - what a meat carton label carries instead of (11)
  raw: string;
}

//AIs with a fixed data length (excluding the AI itself)
const fixedLengthAIs: { [ai: string]: number } = {
  "00": 18,
  "01": 14,
  "02": 14,
  "11": 6,
  "12": 6,
  "13": 6,
  "15": 6,
  "16": 6,
  "17": 6,
  "20": 2,
};

//the FNC1 separator a scanner emits for a variable-length field
const groupSeparator = String.fromCharCode(29);

//variable-length AIs are capped by the standard; a separator ends them early
const variableLengthAIs: { [ai: string]: number } = {
  "10": 20,
  "21": 20,
  "240": 30,
  "241": 30,
  "400": 30,
  "401": 30,
};

//yymmdd -> yyyy-mm-dd. A yy more than 50 years ahead belongs to the previous century.
export const parseGs1Date = (value: string): string | undefined => {
  if (!value || value.length !== 6 || !/^\d{6}$/.test(value)) {
    return undefined;
  }
  const yy = parseInt(value.substring(0, 2), 10);
  const mm = parseInt(value.substring(2, 4), 10);
  let dd = parseInt(value.substring(4, 6), 10);
  if (mm < 1 || mm > 12) {
    return undefined;
  }
  //a dd of 00 means "end of month" in GS1
  if (dd === 0) {
    dd = new Date(2000 + yy, mm, 0).getDate();
  }
  const currentYY = new Date().getFullYear() % 100;
  const century = yy - currentYY > 50 ? 1900 : 2000;
  const year = century + yy;
  return `${year}-${mm.toString().padStart(2, "0")}-${dd.toString().padStart(2, "0")}`;
};

//a measurement AI (310n..316n, 320n..336n) carries its decimal count in the last digit
export const isMeasurementAi = (ai: string): boolean => /^3[123]\d\d$/.test(ai);

export const measurementDecimals = (ai: string): number => {
  return isMeasurementAi(ai) ? parseInt(ai.substring(3, 4), 10) : 0;
};

//raw AI -> data, keeping measurement AIs at their full 4 digits (e.g. "3103")
export const extractAis = (barcode: string): { [ai: string]: string } => {
  const found: { [ai: string]: string } = {};
  if (!barcode) {
    return found;
  }
  //some scanners send the FNC1 as a literal "]C1" prefix or as ASCII 29
  const data = barcode.replace(/^\]C1/, "").trim();
  let pos = 0;

  const readVariable = (max: number): string => {
    const sepAt = data.indexOf(groupSeparator, pos);
    const end = sepAt >= 0 ? Math.min(sepAt, pos + max) : Math.min(data.length, pos + max);
    const value = data.substring(pos, end);
    pos = sepAt >= 0 && sepAt <= pos + max ? sepAt + 1 : end;
    return value;
  };

  while (pos < data.length) {
    //skip stray separators
    if (data[pos] === groupSeparator) {
      pos += 1;
      continue;
    }
    const two = data.substring(pos, pos + 2);
    const three = data.substring(pos, pos + 3);
    const four = data.substring(pos, pos + 4);

    if (isMeasurementAi(four)) {
      pos += 4;
      found[four] = data.substring(pos, pos + 6);
      pos += 6;
      continue;
    }

    if (fixedLengthAIs[two] !== undefined) {
      const len = fixedLengthAIs[two];
      pos += 2;
      found[two] = data.substring(pos, pos + len);
      pos += len;
      continue;
    }

    if (variableLengthAIs[two] !== undefined) {
      pos += 2;
      found[two] = readVariable(variableLengthAIs[two]);
      continue;
    }

    if (variableLengthAIs[three] !== undefined) {
      pos += 3;
      found[three] = readVariable(variableLengthAIs[three]);
      continue;
    }

    //unrecognised AI - stop rather than mis-slice the rest of the barcode
    break;
  }

  return found;
};

//the standard mapping, used when PDATallyGS1BarcodeRules is not set
export const parseGs1Barcode = (barcode: string): Gs1Barcode => {
  const result: Gs1Barcode = { raw: barcode };
  const ais = extractAis(barcode);

  Object.keys(ais).forEach((ai) => {
    const value = ais[ai];
    if (isMeasurementAi(ai)) {
      if (ai.startsWith("310") && /^\d{6}$/.test(value)) {
        result.netWeight = parseInt(value, 10) / Math.pow(10, measurementDecimals(ai));
      }
      return;
    }
    switch (ai) {
      case "01":
        result.gtin = value;
        break;
      case "21":
        result.serialNo = value;
        break;
      case "10":
        result.batch = value;
        break;
      case "17":
        result.useByDate = parseGs1Date(value);
        break;
      case "11":
        result.mfgDate = parseGs1Date(value);
        break;
      case "13":
        result.packDate = parseGs1Date(value);
        break;
      default:
        break;
    }
  });

  return result;
};

//a barcode is only usable as a tally if it carried the serial and a weight
export const isTallyableGs1 = (parsed: Gs1Barcode): boolean => {
  return !!parsed.serialNo && parsed.netWeight !== undefined && parsed.netWeight > 0;
};
